import { Ics2AdditionalInformation, Ics2Referral } from 'store/declarations/eu/ics2/customs-requests/types';

export const FormFields = {
    ADDITIONAL_INFORMATION: 'additionalInformation',
    ACCEPTED_AMENDMENT_REQUEST_ID: 'acceptedAmendmentRequestId',
} as const;

/**
 * CL752 response codes that carry extra obligations. The backend's response builder rejects a
 * submission that breaks any of these, so the form enforces them first.
 */
export const CODES_REQUIRING_TEXT = ['R100', 'R200', 'R202', 'R203'];
export const CODES_REQUIRING_ATTACHMENT = ['R102', 'D01'];
export const CODE_REQUIRING_AMENDMENT = 'R103';
export const CODES_REQUIRING_AMENDMENT_LIST = [CODE_REQUIRING_AMENDMENT];

/** IE3R02 allows up to 99 additional-information entries per referral response. */
export const MAX_INFORMATION_ENTRIES = 99;

export type AdditionalInformationValues = {
    code: string | undefined;
    text: string | undefined;
};

export type FormValues = {
    additionalInformation: AdditionalInformationValues[];
    acceptedAmendmentRequestId: string | undefined;
};

export const emptyInformationEntry: AdditionalInformationValues = { code: undefined, text: undefined };

/**
 * The information type is deliberately absent: CL703 has exactly one valid value on a response
 * (`R2` here, `R4` on IE3R03) and the backend sets it, rejecting anything else.
 */
export const toReferralDraft = (values: FormValues) => ({
    additionalInformation: values.additionalInformation
        .filter((entry) => entry.code || entry.text)
        .map<Ics2AdditionalInformation>((entry) => ({
            code: entry.code || undefined,
            text: entry.text || undefined,
        })),
    acceptedAmendmentRequestId: values.acceptedAmendmentRequestId || undefined,
});

/**
 * The inverse of `toReferralDraft`, kept beside it so the two cannot drift.
 *
 * Used both to seed the form and to re-validate a saved draft without the form being mounted -
 * a card's send can include referrals whose drawer was never opened.
 */
export const fromReferralDraft = (referral: Ics2Referral): FormValues => {
    const saved = referral.responseDraft;
    return {
        additionalInformation: saved?.additionalInformation?.length
            ? saved.additionalInformation.map((entry) => ({ code: entry.code, text: entry.text }))
            : [{ ...emptyInformationEntry }],
        acceptedAmendmentRequestId: saved?.acceptedAmendmentRequestId,
    };
};
